import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Image } from 'primereact/image';
import { Sidebar } from 'primereact/sidebar';
import { Link, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  { label: 'Platform', to: '/#platform' },
  { label: 'Product', to: '/#product' },
  { label: 'AI', to: '/#ai' },
];

const NAV_ITEMS_AFTER_ABOUT = [
  { label: 'Contact', to: '/#contact' },
];

const ABOUT_SUBMENU = [
  { label: 'Meet the Team', to: '/about' },
  { label: 'News', to: '/blog' },
  { label: 'Careers', to: '/careers' },
];

const LOGIN_MENU_ITEMS = [
  { label: 'Patient Login', to: '/login' },
  { label: 'Site Login', to: '/login' },
  { label: 'Sponsor/CRO Login', to: '/login' },
];

const COMPLIANCE_CENTER_URL = 'https://compliance.phennx.io';

const NavBar = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [loginDropdownOpen, setLoginDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const loginDropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAboutDropdownOpen(false);
      }
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(event.target)) {
        setLoginDropdownOpen(false);
      }
    };

    if (aboutDropdownOpen || loginDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [aboutDropdownOpen, loginDropdownOpen]);

  // Close dropdown when route changes
  useEffect(() => {
    setAboutDropdownOpen(false);
    setLoginDropdownOpen(false);
  }, [location.pathname]);

  const openSidebar = useCallback(() => setSidebarVisible(true), []);
  const closeSidebar = useCallback(() => setSidebarVisible(false), []);

  const openComplianceCenter = useCallback(() => {
    window.open(COMPLIANCE_CENTER_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const toggleAboutDropdown = useCallback(() => {
    setAboutDropdownOpen((prev) => !prev);
  }, []);

  const toggleLoginDropdown = useCallback(() => {
    setLoginDropdownOpen((prev) => !prev);
  }, []);

  const isAboutPage = location.pathname === '/about' || location.pathname === '/blog' || location.pathname === '/careers';

  return (
    <>
      <header className={`site-header ${isScrolled ? 'is-scrolled' : ''}`}>
        <div className="site-header__inner">
          <Link to="/" className="brand" aria-label="Go to top">
            <Image
              src="/logo-transparent.png"
              width="40"
              height="40"
              alt="Phennx"
              imageStyle={{ borderRadius: '10px' }}
            />
            <span className="brand__name">PHENNX</span>
          </Link>

          <nav className="site-nav" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <Link key={item.to} className="site-nav__link" to={item.to}>
                {item.label}
              </Link>
            ))}
            <div className="site-nav__dropdown" ref={dropdownRef}>
              <button
                className={`site-nav__link site-nav__link--dropdown ${isAboutPage ? 'is-active' : ''}`}
                onClick={toggleAboutDropdown}
                aria-expanded={aboutDropdownOpen}
                aria-haspopup="true"
              >
                About
                <i className="pi pi-chevron-down site-nav__chevron" aria-hidden="true" />
              </button>
              {aboutDropdownOpen && (
                <div className="site-nav__dropdown-menu">
                  {ABOUT_SUBMENU.map((item) => (
                    <Link
                      key={item.to}
                      className="site-nav__dropdown-item"
                      to={item.to}
                      onClick={() => setAboutDropdownOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {NAV_ITEMS_AFTER_ABOUT.map((item) => (
              <Link key={item.to} className="site-nav__link" to={item.to}>
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="site-header__actions">
            <div className="site-nav__dropdown site-login-dropdown" ref={loginDropdownRef}>
              <button
                type="button"
                className={`site-login-link site-login-link--dropdown ${loginDropdownOpen ? 'is-active' : ''}`}
                onClick={toggleLoginDropdown}
                aria-expanded={loginDropdownOpen}
                aria-haspopup="true"
              >
                Login
                <i className="pi pi-chevron-down site-nav__chevron" aria-hidden="true" />
              </button>
              {loginDropdownOpen && (
                <div className="site-nav__dropdown-menu site-nav__dropdown-menu--align-right site-login-dropdown__menu">
                  {LOGIN_MENU_ITEMS.map((item) => (
                    <Link
                      key={item.label}
                      className="site-nav__dropdown-item"
                      to={item.to}
                      onClick={() => setLoginDropdownOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Button
              label="Compliance Center"
              icon="pi pi-shield"
              className="site-cta"
              onClick={openComplianceCenter}
              aria-label="Compliance Center (opens in a new tab)"
            />
            <Button
              icon="pi pi-bars"
              className="site-menu-btn p-button-rounded p-button-text"
              onClick={openSidebar}
              aria-label="Open menu"
            />
          </div>
        </div>
      </header>

      <Sidebar
        visible={sidebarVisible}
        onHide={closeSidebar}
        position="right"
        className="w-full sm:w-20rem"
        style={{ background: '#070b10', color: '#f2f5f8' }}
      >
        <div className="mobile-menu">
          <div className="mobile-menu__header">Menu</div>

          <div className="mobile-menu__links">
            {NAV_ITEMS.map((item) => (
              <Link key={item.to} className="mobile-menu__link" to={item.to} onClick={closeSidebar}>
                {item.label}
              </Link>
            ))}
            <div className="mobile-menu__section">
              <div className="mobile-menu__section-title">About</div>
              {ABOUT_SUBMENU.map((item) => (
                <Link
                  key={item.to}
                  className="mobile-menu__link mobile-menu__link--sub"
                  to={item.to}
                  onClick={closeSidebar}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            {NAV_ITEMS_AFTER_ABOUT.map((item) => (
              <Link key={item.to} className="mobile-menu__link" to={item.to} onClick={closeSidebar}>
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mobile-menu__actions">
            <div className="mobile-menu__section-title">Login</div>
            {LOGIN_MENU_ITEMS.map((item) => (
              <Link
                key={item.label}
                className="mobile-menu__link mobile-menu__link--action"
                to={item.to}
                onClick={closeSidebar}
              >
                {item.label}
              </Link>
            ))}
            <Button
              label="Compliance Center"
              icon="pi pi-shield"
              className="btn-primary"
              style={{ gap: '0.5rem' }}
              onClick={openComplianceCenter}
              aria-label="Compliance Center (opens in a new tab)"
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default NavBar;
